/**
 * IdleLogout — signs the user out after 5 minutes of inactivity for
 * security, then routes them to the correct login page so they can
 * come right back with their email + password.
 *
 * Activity signals: mousemove, mousedown, keydown, touchstart, scroll,
 * visibilitychange (tab-focus counts as activity). Cross-tab sync via
 * localStorage so idle timer only fires when the user is idle in EVERY
 * open tab.
 *
 * Rendered once at the App root — no props needed. Reads/writes
 * `localStorage.crc_last_activity_at` (Unix ms).
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const IDLE_MS = 5 * 60 * 1000;       // 5 minutes
const CHECK_MS = 15 * 1000;          // check every 15 s
const ACTIVITY_KEY = "crc_last_activity_at";
// Skip the timer on obviously-public routes so we don't kick logged-in
// visitors off the marketing pages between clicks.
const PUBLIC_PREFIXES = [
  "/", "/pricing", "/four-savers", "/free-actions", "/blog",
  "/about", "/laws", "/answers", "/property-management",
  "/rental-market", "/tools", "/login", "/signup", "/forgot",
  "/reset", "/verify", "/watch-it-work", "/fr",
];
function isPublicPath(pathname) {
  return PUBLIC_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export default function IdleLogout() {
  const { user, logout } = useAuth() || {};
  const nav = useNavigate();
  const loc = useLocation();
  const timerRef = useRef(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!user) return;                                   // only for signed-in users
    if (isPublicPath(loc.pathname)) return;              // don't idle-kick from marketing

    const stamp = () => {
      try { localStorage.setItem(ACTIVITY_KEY, String(Date.now())); } catch { /* private mode */ }
      firedRef.current = false;
    };
    stamp();

    const activityEvents = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];
    activityEvents.forEach((e) => window.addEventListener(e, stamp, { passive: true }));

    const onVis = () => { if (document.visibilityState === "visible") stamp(); };
    document.addEventListener("visibilitychange", onVis);

    const check = async () => {
      if (firedRef.current) return;
      let last = 0;
      try { last = parseInt(localStorage.getItem(ACTIVITY_KEY) || "0", 10) || 0; } catch { /* noop */ }
      const idle = Date.now() - last;
      if (idle >= IDLE_MS) {
        firedRef.current = true;
        try { await logout(); } catch { /* noop */ }
        try { localStorage.removeItem(ACTIVITY_KEY); } catch { /* noop */ }
        toast.info("Signed out — you were idle for 5 minutes. Log back in with your email + password.", { duration: 6000 });
        // Route to the correct login portal based on where they were.
        const path = loc.pathname || "";
        const portal =
          path.startsWith("/tenant") || path.includes("tenant") ? "tenant" :
          path.startsWith("/admin") ? "admin" : "landlord";
        nav(`/login/${portal}?reason=idle`, { replace: true });
      }
    };
    timerRef.current = setInterval(check, CHECK_MS);

    return () => {
      activityEvents.forEach((e) => window.removeEventListener(e, stamp));
      document.removeEventListener("visibilitychange", onVis);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [user, loc.pathname, logout, nav]);

  return null;
}
